import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import { HostPrimaryButton } from '../components/HostSettingsCard';
import { HostOnboardingShell } from '../components/host-onboarding/HostOnboardingShell';
import { getRoleSwitchContext } from '../utils/roleSwitchFlow';

export default function HostIntro1Page() {
  const navigate = useNavigate();
  // Two entry points land users here:
  //   1. Fresh signup (email or Google): /select-role → tap "Airbnb Host"
  //      → /host-intro-1. These users MUST go back to /select-role, even
  //      though they already have a token (Google auth mints one before
  //      they pick a role).
  //   2. Existing multi-role user: /switch-role → tap "Airbnb Host" →
  //      /host-intro-1. Only these users should return to /switch-role.
  // Using token presence conflates the two — the durable signal is
  // roleSwitchContext, which SwitchRolePage sets right before navigate.
  const goBack = () => {
    const cameFromSwitchRole = Boolean(getRoleSwitchContext());
    navigate(cameFromSwitchRole ? '/switch-role' : '/select-role');
  };

  return (
    <HostOnboardingShell
      onBack={goBack}
      pageTestId="host-intro-1-page"
      backTestId="host-intro-1-back-button"
      logoTestId="host-intro-1-logo"
      footer={
        <HostPrimaryButton onClick={() => navigate('/host-intro-2')} dataTestId="host-intro-1-next-button">
          Next
        </HostPrimaryButton>
      }
    >
      <motion.div
        data-testid="host-intro-1-icon"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
        style={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          background: 'rgba(196, 18, 48, 0.12)',
          border: '1px solid rgba(196, 18, 48, 0.22)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 20,
        }}
      >
        <TrendingUp size={40} color="var(--accent-purple)" strokeWidth={1.5} />
      </motion.div>

      <motion.h1
        data-testid="host-intro-1-title"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
        style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 25,
          fontWeight: 700,
          color: 'var(--text)',
          textAlign: 'center',
          marginBottom: 18,
          lineHeight: 1.05,
        }}
      >
        Help your guests explore more, while you earn more!
      </motion.h1>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
        className="premium-card"
        data-testid="host-intro-1-message-card"
        style={{ width: '100%', padding: '24px 18px' }}
      >
        <p
          data-testid="host-intro-1-message"
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 19,
            fontWeight: 700,
            color: 'var(--text)',
            textAlign: 'center',
            lineHeight: 1.15,
            margin: 0,
          }}
        >
          Turn every stay into a premium experience. Invite your guests to unlock exclusive perks at the finest venues nearby.
        </p>
      </motion.div>
    </HostOnboardingShell>
  );
}
